import { useCountries, useStates, useCities } from "../geo/hooks";
import InfoTip from "../../components/ui/InfoTip";

export interface OfficeLocation {
  country: string;
  country_code?: string;
  state?: string;
  state_code?: string;
  city: string;
  address?: string;
  zip_code?: string;
}

interface MultiOfficeInputFieldProps {
  label: string;
  infoTip?: string;
  required?: boolean;
  values: OfficeLocation[];
  onChange: (values: OfficeLocation[]) => void;
  /** Values (country/state/city, matched case-insensitively) awaiting admin
   *  verification — shown with a "pending verification" note on the office card. */
  pendingValues?: string[];
  disabledItemIndex?: number;
}

function OfficeLocationForm({
  value,
  onChange,
  onRemove,
  isRemovable,
  pendingValues,
  disabled,
}: {
  value: OfficeLocation;
  onChange: (val: OfficeLocation) => void;
  onRemove: () => void;
  isRemovable: boolean;
  pendingValues?: string[];
  disabled?: boolean;
}) {
  const { countries, loading: loadingCountries } = useCountries();

  const pendingSet = new Set((pendingValues || []).map((p) => p.toLowerCase()));
  const isPending =
    (!!value.country && pendingSet.has(value.country.toLowerCase())) ||
    (!!value.state && pendingSet.has(value.state.toLowerCase())) ||
    (!!value.city && pendingSet.has(value.city.toLowerCase()));

  // Saved data may hold either the ISO-2 code or the full country/state name
  // (older records were written before this component stored codes
  // explicitly, and some are back-filled server-side from legacy flat
  // fields). Resolve against both so lookups and prefill work either way.
  const selectedCountry = countries.find(
    (c) => c.iso2 === value.country_code || c.iso2 === value.country || c.name === value.country,
  );
  const countryIso2 = selectedCountry?.iso2 || "";

  const { states, loading: loadingStates } = useStates(countryIso2);

  const selectedState = states.find(
    (s) => s.isoCode === value.state_code || s.isoCode === value.state || s.name === value.state,
  );
  const stateIso2 = selectedState?.isoCode || "";

  const { cities, loading: loadingCities } = useCities(countryIso2, stateIso2);

  // GST-derived city names come from free-text address parsing and can differ
  // in case/whitespace from the geo API's city list (e.g. "Gurugram" from the
  // GST address vs "Gurugram " or different casing from the cities endpoint),
  // so match case-insensitively and fall back to the raw value as an extra
  // option rather than silently showing a blank "Select City".
  const matchedCity = cities.find(
    (c) => c.name.trim().toLowerCase() === value.city.trim().toLowerCase(),
  );
  const cityOptions = matchedCity || !value.city
    ? cities
    : [{ name: value.city }, ...cities];
  const cityValue = matchedCity?.name ?? value.city;

  return (
    <div className={`rounded-xl border p-4 shadow-sm mb-4 ${isPending ? "border-amber-300 bg-amber-50/40" : "border-border bg-surface"}`}>
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-ink">Office Location</h4>
          {isPending && (
            <span
              title="Awaiting admin verification — deals here can't publish yet"
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-300"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Pending verification
            </span>
          )}
        </div>
        {isRemovable && !disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 text-xs font-medium"
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Country */}
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">Country *</label>
          <select
            value={countryIso2}
            onChange={(e) => {
              const c = countries.find((c) => c.iso2 === e.target.value);
              onChange({
                ...value,
                country: c?.name || "",
                country_code: c?.iso2 || "",
                state: "",
                state_code: "",
                city: "",
              });
            }}
            className="input w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={disabled}
          >
            <option value="">{loadingCountries ? "Loading..." : "Select Country"}</option>
            {countries.map((c) => (
              <option key={c.iso2} value={c.iso2}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* State */}
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">State / Province</label>
          <select
            value={stateIso2}
            onChange={(e) => {
              const s = states.find((s) => s.isoCode === e.target.value);
              onChange({
                ...value,
                state: s?.name || "",
                state_code: s?.isoCode || "",
                city: "",
              });
            }}
            className="input w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={disabled || !countryIso2 || loadingStates}
          >
            <option value="">{loadingStates ? "Loading..." : "Select State (Optional)"}</option>
            {states.map((s) => (
              <option key={s.isoCode} value={s.isoCode}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* City */}
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">City *</label>
          {cityOptions.length > 0 ? (
            <select
              value={cityValue}
              onChange={(e) => onChange({ ...value, city: e.target.value })}
              className="input w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={disabled || !countryIso2 || loadingCities}
            >
              <option value="">{loadingCities ? "Loading..." : "Select City"}</option>
              {cityOptions.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={value.city}
              onChange={(e) => onChange({ ...value, city: e.target.value })}
              placeholder="Enter City"
              className="input w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={disabled}
            />
          )}
        </div>

        {/* ZIP / Pincode */}
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">Pincode / ZIP</label>
          <input
            type="text"
            value={value.zip_code || ""}
            onChange={(e) => onChange({ ...value, zip_code: e.target.value })}
            placeholder="e.g. 10001"
            className="input w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium text-ink-muted">Address</label>
        <textarea
          value={value.address || ""}
          onChange={(e) => onChange({ ...value, address: e.target.value })}
          placeholder="Street address, building, floor..."
          className="input w-full text-sm min-h-[60px] disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function MultiOfficeInputField({
  label,
  infoTip,
  required,
  values = [],
  onChange,
  pendingValues,
  disabledItemIndex,
}: MultiOfficeInputFieldProps) {
  const offices = Array.isArray(values) && values.length > 0 ? values : [{ country: "", city: "" }];

  const updateOffice = (index: number, newOffice: OfficeLocation) => {
    const updated = [...offices];
    updated[index] = newOffice;
    onChange(updated);
  };

  const removeOffice = (index: number) => {
    if (offices.length <= 1) return;
    const updated = offices.filter((_, i) => i !== index);
    onChange(updated);
  };

  const addOffice = () => {
    onChange([...offices, { country: "", city: "" }]);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-ink">
            {label} {required && <span className="text-red-500">*</span>}
          </span>
          {infoTip && <InfoTip message={infoTip} />}
        </div>
        <button
          type="button"
          onClick={addOffice}
          className="text-xs font-semibold text-brand hover:underline"
        >
          + Add another office
        </button>
      </div>

      <div className="space-y-3">
        {offices.map((office, i) => (
          <OfficeLocationForm
            key={i}
            value={office}
            onChange={(val) => updateOffice(i, val)}
            onRemove={() => removeOffice(i)}
            isRemovable={offices.length > 1}
            pendingValues={pendingValues}
            disabled={i === disabledItemIndex}
          />
        ))}
      </div>
    </div>
  );
}
